"use client";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import JsonLdLocalBusiness from "@/components/JsonLdLocalBusiness";
import { getTranslations } from "@/i18n/server";
import { YnsLink } from "@/ui/yns-link";
import { motion, useInView } from "framer-motion";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

// This is a Client Component that takes props from the Server Component
interface HeroButtonProps {
	href: string;
	text: string;
}

const HeroButton = ({ href, text }: HeroButtonProps) => {
	return (
		<motion.div
			className="inline-block mb-6"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<motion.div
				className="relative"
				whileHover={{ scale: 1.05 }}
				whileTap={{ scale: 0.98 }}
				transition={{ type: "spring", stiffness: 400, damping: 17 }}
			>
				{/* Glow effect */}
				<motion.div
					className="absolute inset-0 rounded-md bg-red-500 opacity-50 blur-md"
					whileHover={{ scale: 1.2, opacity: 0.8 }}
					transition={{ duration: 0.3 }}
				/>

				{/* Button */}
				<YnsLink
					className="relative rounded-md bg-red-600 px-8 py-3 font-amarante text-2xl text-white transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-red-400 font-black text-shadow-sm"
					href={href}
				>
					<motion.span
						className="relative inline-block"
						whileHover={{ textShadow: "0 0 8px rgba(255,255,255,0.8), 0 0 12px rgba(255,50,50,0.4)" }}
					>
						{text}
					</motion.span>
					{/* Pulsating Glow effect */}
					<motion.div
						className="absolute inset-0 rounded-md pointer-events-none"
						initial={{
							scale: 1,
							boxShadow: "0 0 10px rgba(255, 50, 50, 0.4)",
						}}
						animate={{
							scale: [1, 1.02, 1],
							boxShadow: [
								"0 0 10px rgba(255, 50, 50, 0.4)",
								"0 0 20px rgba(255, 50, 50, 0.6)",
								"0 0 10px rgba(255, 50, 50, 0.4)",
							],
						}}
						transition={{
							duration: 2,
							repeat: Infinity,
							repeatType: "loop",
							ease: "easeInOut",
						}}
						whileHover={{
							scale: 1.05,
							boxShadow: "0 0 30px rgba(255, 80, 80, 0.9)",
							transition: { duration: 0.3 },
						}}
					/>

					{/* Particles effect on hover */}
					<motion.div className="absolute inset-0" whileHover="hover">
						{Array.from({ length: 8 }).map((_, index) => (
							<motion.div
								key={index}
								className="absolute w-1 h-1 rounded-full bg-red-300"
								initial={{ opacity: 0, scale: 0 }}
								variants={{
									hover: {
										opacity: [0, 1, 0],
										scale: [0, 1, 0],
										x: (Math.random() - 0.5) * 100,
										y: (Math.random() - 0.5) * 100,
										transition: {
											duration: 0.8,
											repeat: Infinity,
											repeatDelay: Math.random() * 0.3,
										},
									},
								}}
								style={{
									left: `${Math.random() * 100}%`,
									top: `${Math.random() * 100}%`,
								}}
							/>
						))}
					</motion.div>
				</YnsLink>
			</motion.div>
		</motion.div>
	);
};

// Separate PulsingTitle component to handle the animated title
const PulsingTitle = ({ text }: { text: string }) => {
	return (
		<motion.div
			className="mainTitleFont text-5xl md:text-7xl mb-4 drop-shadow-lg relative"
			initial={{ opacity: 0, y: -20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<motion.span
				className="inline-block"
				animate={{
					textShadow: [
						"0 0 15px rgba(255, 255, 255, 0.5)",
						"0 0 30px rgba(255, 255, 255, 0.8)",
						"0 0 15px rgba(255, 255, 255, 0.5)",
					],
					scale: [1, 1.03, 1],
				}}
				transition={{
					duration: 3,
					repeat: Infinity,
					repeatType: "loop",
					ease: "easeInOut",
				}}
				whileHover={{
					scale: 1.05,
					textShadow: "0 0 40px rgba(255, 255, 255, 0.9), 0 0 20px rgba(255, 50, 50, 0.6)",
					transition: { duration: 0.3 },
				}}
			>
				{text}
			</motion.span>

			{/* Subtle particles around the title */}
			<motion.div className="absolute inset-0 pointer-events-none overflow-visible">
				{Array.from({ length: 12 }).map((_, index) => (
					<motion.div
						key={index}
						className="absolute w-1 h-1 rounded-full bg-white opacity-70"
						animate={{
							opacity: [0, 0.7, 0],
							scale: [0, 1, 0],
							x: (Math.random() - 0.5) * 150,
							y: (Math.random() - 0.5) * 80,
						}}
						transition={{
							duration: 3,
							repeat: Infinity,
							repeatDelay: Math.random() * 2,
							ease: "easeInOut",
							delay: Math.random() * 2,
						}}
						style={{
							left: `${Math.random() * 100}%`,
							top: `${Math.random() * 100}%`,
						}}
					/>
				))}
			</motion.div>
		</motion.div>
	);
};

interface FeatureItemProps {
	icon: string;
	title: string;
	text: string;
	index: number;
	page: string;
}

interface Review {
	name: string;
	text: string;
	rating: number;
}

const ReviewsSlider = () => {
	const [currentSlide, setCurrentSlide] = useState<number>(0);
	const [loaded, setLoaded] = useState<boolean>(false);
	const [shuffledReviews, setShuffledReviews] = useState<Review[]>([]); // Properly typed here

	// Shuffle reviews on component mount
	useEffect(() => {
		const reviews: Review[] = [
			{
				name: "Esteban Mesón de Arana",
				text: "Si estáis deseando meteros en el fascinante mundo de las campañas de rol de Dungeons & Dragons, Sergio es el mejor Game Master al que podéis recurrir para que narre y dirija vuestras partidas, y el primero que recomendaría para organizar una campaña de rol con familiares y amigos. \n\nSuper amigable con todas las personas con las que juega en cada partida, y su manera de narrar y conducir las partidas de rol, super divertida y accesible para todo el mundo, tanto para novatos como para expertos en el mundo del rol. \n\nMuy buenos ratos jugando a rol garantizados.",
				rating: 5,
			},
			{
				name: "Bea Lucas",
				text: "Ha sido un planazo. Se lo hemos regalado a nuestro hijo de 14 años y a su hermana y nos lo hemos pasado en grande los 4. Ha sido una aventura de dragones y mazmorras adaptada para que no fuera taaan larga y ha sido todo un éxito. Muy divertido! Nuestro game Master ha sido Sergio y se lo ha currado un montón! Súper plan en familia.",
				rating: 5,
			},
			{
				name: "Carlos Meson (Caryest)",
				text: "Una experiencia increíble, con Sergio siempre nos lo pasamos súper bien, y recomiendo mucho este tipo de servicios para personas que quieran jugar DnD con un DM profesional ¡Nuestra experiencia navideña anual que nunca falta!",
				rating: 5,
			},
			{
				name: "Danyel Penia",
				text: "Una experiencia genial. Era nuestra primera vez con este tipo de juegos y nos lo hizo sencillo y muy divertido. Sin duda una gran experiencia, ¡repetiremos!",
				rating: 5,
			},
			{
				name: "Paloma Mesón de Arana",
				text: "Fue nuestra primera vez con un DM profesional y no podemos estar más satisfechos. Historia divertida y bien balanceada, con una dirección genial. ¡Ya estamos pensando en la próxima!",
				rating: 5,
			},
			{
				name: "Almudena G",
				text: "Muy divertido, ¡repetiremos seguro! 🥰",
				rating: 5,
			},
			{
				name: "Alex Sagrado",
				text: "Nunca había jugado al rol y lo hemos pasado de maravilla. Risas, estrategia, acción... ¡planazo del finde!",
				rating: 5,
			},
			{
				name: "Comunitario Almendrales Irene",
				text: "Sergio organizó una partida para 20 chavales/as de Usera. ¡Un reto superado! Se lo pasaron genial y desataron su imaginación. ¡Gracias!",
				rating: 5,
			},
			{
				name: "Elisa Hernández",
				text: "Recomendable al 100%. Jugamos Mareando el Carnero y nos lo pasamos muy bien. Fueron muy flexibles y Sergio, encantador.",
				rating: 5,
			},
			{
				name: "Jorge Gutierrez",
				text: "Muy contento con la sesión. El GM ayudó muchísimo a quienes no teníamos experiencia. ¡Llevó la campaña genial!",
				rating: 5,
			},
			{
				name: "Mónica Gómez",
				text: "Primera vez jugando y fue una gran experiencia. El tiempo se pasó volando. Sergio nos guió con mucha paciencia.",
				rating: 5,
			},
			{
				name: "Diego C.R. (Equis Más Uno)",
				text: "Queríamos una partida de iniciación y nos lo pasamos genial. Sergio nos llevó por una historia divertida y nos hizo reír un montón.",
				rating: 5,
			},
			{
				name: "Sofía Maura",
				text: "Regalo sorpresa y experiencia genial. Amamos el universo ESDLA y fue increíble vivir una aventura así. Sergio nos guió con infinita paciencia. ¡Repetiremos!",
				rating: 5,
			},
			{
				name: "Zar Pablo",
				text: "Todo genial: desde el primer contacto hasta la partida. Hice peticiones especiales y Sergio las integró perfectamente. ¡Muy divertido!",
				rating: 5,
			},
			{
				name: "David Benito González",
				text: "Contraté una sesión con DM y fue de 10. Divertida, dinámica y bien dirigida. ¡Volveré sin duda!",
				rating: 5,
			},
			{
				name: "Maroto bambinomonkey",
				text: "Regalé una sesión a mi pareja que nunca había jugado. ¡Fue divertidísima! Sergio lo hizo fácil, y hasta a mí me encantó. ¡Repetiremos!",
				rating: 5,
			},
			{
				name: "Miguel Vigil Oliva",
				text: "Muy buenas partidas, ambientación genial y Sergio es un crack. Lo recomiendo tanto para empezar como para veteranos.",
				rating: 5,
			},
			{
				name: "David Ávila",
				text: "Muy divertido y ameno. Después de destruir trolls y un mago tenebroso, ¡uno se siente un héroe otra vez! Jajaja",
				rating: 5,
			},
		];
		// Fisher-Yates shuffle algorithm with proper typing
		const shuffleArray = (array: Review[]): Review[] => {
			const newArray = [...array];
			for (let i = newArray.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[newArray[i], newArray[j]] = [newArray[j]!, newArray[i]!];
			}
			return newArray;
		};

		setShuffledReviews(shuffleArray(reviews));
	}, []);

	const [sliderRef, slider] = useKeenSlider({
		initial: 0,
		slideChanged(s) {
			setCurrentSlide(s.track.details.rel);
		},
		created() {
			setLoaded(true);
			//startAutoPlay();
		},
		loop: true,
		slides: {
			perView: 1.2,
			spacing: 16,
		},
		breakpoints: {
			"(min-width: 640px)": {
				slides: {
					perView: 2.2,
					spacing: 24,
				},
			},
			"(min-width: 1024px)": {
				slides: {
					perView: 3,
					spacing: 32,
				},
			},
		},
	});

	/*let autoPlay: NodeJS.Timeout;
	const startAutoPlay = () => {
		autoPlay = setInterval(() => {
			if (slider.current) {
				slider.current.next();
			}
		}, 6000); // 6 seconds between slides
	};

	useEffect(() => {
		return () => {
			clearInterval(autoPlay);
		};
	}, []);
	*/

	return (
		<section className="w-full py-20 px-4 bg-gradient-to-b from-black to-red-900/10 relative">
			<div className="max-w-6xl mx-auto">
				<motion.h2
					className="text-4xl font-amarante text-center mb-12 text-white"
					initial={{ opacity: 0, y: -20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
				>
					<span className="inline-block pb-2 border-b-2 border-red-700">Opiniones de nuestros jugadores</span>
				</motion.h2>

				<div className="relative">
					{loaded && slider.current && (
						<>
							<button
								onClick={() => slider.current?.prev()}
								className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-red-900/80 p-3 rounded-full text-white ml-[-20px] transition-colors"
								aria-label="Anterior"
							>
								<ChevronLeft className="w-8 h-8" />
							</button>
							<button
								onClick={() => slider.current?.next()}
								className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-red-900/80 p-3 rounded-full text-white mr-[-20px] transition-colors"
								aria-label="Siguiente"
							>
								<ChevronRight className="w-8 h-8" />
							</button>
						</>
					)}

					{shuffledReviews.length > 0 && (
						<div ref={sliderRef} className="keen-slider pb-8">
							{shuffledReviews.map((review, index) => (
								<div key={`${review.name}-${index}`} className="keen-slider__slide">
									<div
										className="bg-gray-900 p-6 rounded-lg h-full flex flex-col border-2 border-red-900/50 hover:border-red-600/60 transition-colors duration-300"
										//onMouseEnter={() => clearInterval(autoPlay)}
										//onMouseLeave={() => startAutoPlay()}
									>
										<div className="flex mb-4">
											{[...Array(5)].map((_, i) => (
												<Star key={i} className="w-5 h-5 fill-red-500 text-red-500" />
											))}
										</div>
										<p className="text-gray-300 mb-6 flex-grow">"{review.text}"</p>
										<div className="mt-auto">
											<p className="font-amarante text-white text-xl">{review.name}</p>
											<YnsLink
												href="https://maps.app.goo.gl/ivHwSLeoJvs6BHDq5"
												className="text-red-400 hover:text-red-300 text-sm mt-2 inline-block"
												target="_blank"
												rel="noopener noreferrer"
											>
												Ver en Google Maps
											</YnsLink>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{loaded && slider.current && (
					<div className="flex justify-center mt-4">
						{[...Array(slider.current.track.details.slides.length)].map((_, idx) => {
							return (
								<button
									key={idx}
									onClick={() => {
										slider.current?.moveToIdx(idx);
									}}
									className={`w-3 h-3 mx-1 rounded-full ${currentSlide === idx ? "bg-red-500" : "bg-gray-600"}`}
									aria-label={`Ir a slide ${idx + 1}`}
								/>
							);
						})}
					</div>
				)}

				<div className="text-center mt-12">
					<YnsLink
						href="https://maps.app.goo.gl/ivHwSLeoJvs6BHDq5"
						className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-amarante text-lg transition-colors"
						target="_blank"
						rel="noopener noreferrer"
					>
						Ver todas las opiniones
					</YnsLink>
				</div>
			</div>
		</section>
	);
};

// Feature item component with animation on scroll and hover
const FeatureItem = ({ icon, title, text, index, page }: FeatureItemProps) => {
	const itemRef = useRef(null);
	const isInView = useInView(itemRef, { once: true, margin: "-50px 0px" }); // Increased trigger area

	// Different animations based on index
	const getAnimationVariants = () => {
		// Faster base delay and shorter durations
		const baseDelay = 0.05 + index * 0.1; // Reduced from 0.1 + index * 0.2

		// Different entrance animations based on position - with faster timing
		const animations = [
			// First row
			{
				hidden: { opacity: 0, y: 50, rotateZ: -5, scale: 0.9 },
				visible: {
					opacity: 1,
					y: 0,
					rotateZ: 0,
					scale: 1,
					transition: {
						duration: 0.5, // Reduced from 0.7
						delay: baseDelay,
						ease: [0.25, 0.1, 0.25, 1.0],
					},
				},
			},
			{
				hidden: { opacity: 0, y: 70, scale: 0.7 },
				visible: {
					opacity: 1,
					y: 0,
					scale: 1,
					transition: {
						duration: 0.5, // Reduced from 0.8
						delay: baseDelay,
						type: "spring",
						stiffness: 150, // Increased stiffness for faster motion
						damping: 12,
					},
				},
			},
			{
				hidden: { opacity: 0, y: 50, rotateZ: 5, scale: 0.9 },
				visible: {
					opacity: 1,
					y: 0,
					rotateZ: 0,
					scale: 1,
					transition: {
						duration: 0.5, // Reduced from 0.7
						delay: baseDelay,
						ease: [0.25, 0.1, 0.25, 1.0],
					},
				},
			},
			// Second row
			{
				hidden: { opacity: 0, x: -60, scale: 0.8 }, // Reduced from -80
				visible: {
					opacity: 1,
					x: 0,
					scale: 1,
					transition: {
						duration: 0.6, // Reduced from 0.9
						delay: baseDelay,
						type: "spring",
						bounce: 0.3, // Slightly reduced bounce
					},
				},
			},
			{
				hidden: { opacity: 0, y: 80, rotateX: 25 }, // Reduced from 100 and 45
				visible: {
					opacity: 1,
					y: 0,
					rotateX: 0,
					transition: {
						duration: 0.6, // Reduced from 0.9
						delay: baseDelay,
						type: "spring",
						stiffness: 120, // Increased from 80
					},
				},
			},
			{
				hidden: { opacity: 0, x: 60, scale: 0.8 }, // Reduced from 80
				visible: {
					opacity: 1,
					x: 0,
					scale: 1,
					transition: {
						duration: 0.6, // Reduced from 0.9
						delay: baseDelay,
						type: "spring",
						bounce: 0.3, // Slightly reduced bounce
					},
				},
			},
		];

		return animations[index % animations.length];
	};

	// Different hover animations for icons based on index - enhanced for red/black theme
	const getIconHoverAnimation = () => {
		const hoverAnimations = [
			// 1. Fire spin - rotating glow with particles
			{
				initial: {
					rotate: 0,
					boxShadow: "0 0 0px rgba(220, 38, 38, 0)",
				},
				hover: {
					rotate: [0, 360],
					boxShadow: "0 0 30px rgba(220, 38, 38, 0.9), inset 0 0 20px rgba(153, 27, 27, 0.8)",
					transition: {
						rotate: { duration: 3, repeat: Infinity, ease: "linear" }, // Faster rotation
						boxShadow: { duration: 0.2 },
					},
				},
			},
			// 2. Pulse with flame effect
			{
				initial: {
					scale: 1,
					boxShadow: "0 0 0px rgba(220, 38, 38, 0)",
				},
				hover: {
					scale: [1, 1.2, 1],
					boxShadow: "0 0 35px rgba(239, 68, 68, 0.9), inset 0 0 25px rgba(220, 38, 38, 0.8)",
					transition: {
						scale: { duration: 1.2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }, // Faster
						boxShadow: { duration: 0.2 },
					},
				},
			},
			// 3. Energetic bounce with electric effect
			{
				initial: {
					y: 0,
					boxShadow: "0 0 0px rgba(220, 38, 38, 0)",
				},
				hover: {
					y: [0, -10, 0, -5, 0, -2, 0],
					boxShadow: "0 0 20px #ff0000, 0 0 30px rgba(220, 38, 38, 0.8)",
					transition: {
						y: { duration: 0.6, times: [0, 0.3, 0.5, 0.65, 0.8, 0.9, 1] }, // Faster bounce
						boxShadow: { duration: 0.2 },
					},
				},
			},
			// 4. 3D flip with perspective
			{
				initial: {
					rotateY: 0,
					boxShadow: "0 0 0px rgba(185, 28, 28, 0)",
					scale: 1,
				},
				hover: {
					rotateY: [0, 180, 360],
					boxShadow: "0 0 30px rgba(185, 28, 28, 0.9)",
					scale: [1, 1.15, 1],
					transition: {
						duration: 1.5, // Faster
						times: [0, 0.5, 1],
						ease: "easeInOut",
					},
				},
			},
			// 5. Vortex effect - spiral and zoom
			{
				initial: {
					scale: 1,
					rotate: 0,
					boxShadow: "0 0 0px rgba(220, 38, 38, 0)",
				},
				hover: {
					scale: [1, 1.25, 1],
					rotate: [0, 20, -20, 0],
					boxShadow: "0 0 35px rgba(220, 38, 38, 1), inset 0 0 25px rgba(239, 68, 68, 0.8)",
					transition: {
						duration: 1, // Faster
						ease: "easeInOut",
					},
				},
			},
			// 6. Explosive glow
			{
				initial: {
					boxShadow: "0 0 0 rgba(255,0,0,0)",
					scale: 1,
				},
				hover: {
					boxShadow: ["0 0 0 rgba(255,0,0,0)", "0 0 50px rgba(255,0,0,1)", "0 0 25px rgba(255,0,0,0.6)"],
					scale: [1, 1.2, 1.05],
					transition: {
						duration: 0.6, // Faster
						times: [0, 0.5, 1],
						ease: [0.19, 1, 0.22, 1],
					},
				},
			},
		];

		return hoverAnimations[index % hoverAnimations.length];
	};

	const iconAnimation = getIconHoverAnimation();
	if (!iconAnimation) {
		console.error("iconAnimation is undefined");
		// or throw an error, or provide a default value
	}
	return (
		<motion.div
			ref={itemRef}
			className="flex flex-col items-center px-6 py-8 bg-gray-900 rounded-lg shadow-xl h-full border border-gray-800 hover:border-red-600/60 transition-colors duration-300 cursor-pointer"
			onClick={() => window.open(page, "self")}
			// Animation on scroll
			variants={getAnimationVariants()}
			initial="hidden"
			animate={isInView ? "visible" : "hidden"}
			whileHover={{
				backgroundColor: "rgba(15, 20, 30, 0.98)",
				y: -5,
				boxShadow: "0 10px 30px -10px rgba(220, 38, 38, 0.6), 0 4px 6px -2px rgba(0, 0, 0, 0.6)",
				transition: { duration: 0.2 }, // Faster response
			}}
		>
			{/* Subtle background pattern for adventure feel */}
			<motion.div
				className="absolute inset-0 z-0 opacity-30 pointer-events-none rounded-lg overflow-hidden"
				initial={{ opacity: 0 }}
				animate={{ opacity: 0.15 }}
			/>

			<motion.div
				className="relative mb-4 rounded-full overflow-hidden p-3 bg-black w-24 h-24 flex items-center justify-center border-2 border-red-800/50"
				initial={iconAnimation?.initial ?? { rotate: 0, scale: 1, boxShadow: "none" }}
				whileHover="hover"
				variants={iconAnimation}
			>
				<img src={`/images/${icon}`} alt={title} className="w-16 h-16 object-contain z-10" />

				{/* Enhanced background pattern */}
				<motion.div
					className="absolute inset-0 opacity-30"
					style={{
						backgroundImage: "radial-gradient(circle, rgba(153, 27, 27, 0.5) 2px, transparent 2px)",
						backgroundSize: "12px 12px",
					}}
					animate={{
						opacity: [0.3, 0.5, 0.3],
						scale: [1, 1.1, 1],
					}}
					transition={{
						duration: 3,
						repeat: Infinity,
						ease: "easeInOut",
					}}
				/>

				{/* Add effect specific to certain items - enhanced */}
				{index % 3 === 0 && (
					<motion.div
						className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/40 to-red-600/0 rounded-full"
						animate={{
							x: ["-100%", "100%"],
						}}
						transition={{
							duration: 1.5, // Faster
							repeat: Infinity,
							repeatDelay: 2,
						}}
					/>
				)}

				{/* Enhanced pulsing border */}
				{index % 3 === 1 && (
					<motion.div
						className="absolute inset-0 rounded-full border-2 border-red-600/60 pointer-events-none"
						animate={{
							scale: [1, 1.15, 1],
							opacity: [0.6, 0.9, 0.6],
						}}
						transition={{
							duration: 2, // Faster
							repeat: Infinity,
							ease: "easeInOut",
						}}
					/>
				)}

				{/* Enhanced fiery particles */}
				{index % 3 === 2 && (
					<>
						{Array.from({ length: 10 }).map((_, i) => (
							<motion.div
								key={i}
								className="absolute w-1 h-1 rounded-full bg-red-600"
								initial={{
									opacity: 0,
									scale: 0,
									x: 0,
									y: 0,
								}}
								animate={{
									opacity: [0, 0.9, 0],
									scale: [0, 1, 0.5],
									x: [0, (Math.random() - 0.5) * 70],
									y: [0, (Math.random() - 0.5) * 70],
								}}
								transition={{
									duration: 1.5, // Faster
									repeat: Infinity,
									delay: Math.random() * 1.5,
									repeatDelay: Math.random() * 1.5,
								}}
								style={{
									left: "50%",
									top: "50%",
								}}
							/>
						))}
					</>
				)}
			</motion.div>

			<motion.h3
				className="font-amarante text-xl font-bold mb-3 text-white text-center relative"
				initial={{ opacity: 0 }}
				animate={isInView ? { opacity: 1 } : { opacity: 0 }}
				transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }} // Faster
				style={{
					textShadow:
						"0.5px 0 black, -0.5px 0 black, 0 0.5px black, 0 -0.5px black, 1px 1px 0 red, 1.5px 1.5px 0 black",
				}}
			>
				{title}
				<motion.div
					className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 rounded-full"
					initial={{ width: 0 }}
					animate={isInView ? { width: "90%" } : { width: 0 }}
					transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }} // Faster
				/>
			</motion.h3>

			<motion.p
				className="text-center text-gray-300"
				initial={{ opacity: 0 }}
				animate={isInView ? { opacity: 1 } : { opacity: 0 }}
				transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }} // Faster
			>
				{text}
			</motion.p>

			{/* New: Adding subtle flame effect at the bottom for adventure feel */}
			<motion.div
				className="absolute bottom-0 left-0 right-0 h-2 overflow-hidden rounded-b-lg"
				initial={{ opacity: 0 }}
				whileHover={{ opacity: 1 }}
			>
				{Array.from({ length: 15 }).map((_, i) => (
					<motion.div
						key={i}
						className="absolute bottom-0 w-3 h-4 bg-gradient-to-t from-red-700 to-red-500/0"
						animate={{
							height: [10, 16, 10],
							x: [Math.random() * -5, Math.random() * 5, Math.random() * -5],
						}}
						transition={{
							duration: 0.8 + Math.random() * 0.5,
							repeat: Infinity,
							repeatType: "reverse",
							ease: "easeInOut",
							delay: Math.random() * 0.5,
						}}
						style={{
							left: `${(100 / 15) * i}%`,
							borderRadius: "50% 50% 0 0",
							filter: "blur(2px)",
							opacity: 0.7 + Math.random() * 0.3,
						}}
					/>
				))}
			</motion.div>
		</motion.div>
	);
};

export default async function Home() {
	// Server Component with translations
	const t = await getTranslations("/");

	// Feature items data con textos SEO optimizados
	const featureItems = [
		{
			icon: "aventuras.webp",
			title: "Aventuras de Rol en Madrid",
			text: "Vive historias épicas dirigidas por un Master de Rol Profesional. Desde jugar a Dragones y Mazmorras en Madrid hasta tramas de intriga, diseñamos aventuras a medida para que disfrutes del rol sin límites.",
			page: "category/aventuras",
		},
		{
			icon: "precios.webp",
			title: "Precios de Partidas Rol en Madrid",
			text: "Tarifas flexibles para disfrutar de la experiencia de un Master de Rol Profesional. Packs adaptados para campañas de larga duración o sesiones de D&D y otros sistemas en Madrid.",
			page: "/category/precios",
		},
		{
			icon: "eventos.webp",
			title: "Eventos de Rol Madrid ● Cumpleaños ● Team Building",
			text: "Llevamos el rol a tu celebración con un Master de Rol Profesional. Somos expertos en organizar eventos para jugar a Dungeons & Dragons en Madrid, ideales para empresas y cumpleaños originales, pero también sistemas mucho más sencillos que harán las horas fluir sin darte cuenta",
			page: "eventos",
		},
		{
			icon: "sistemas.webp",
			title: "Sistemas de Rol",
			text: "Explora nuevos mundos con un Master de Rol Profesional. Te enseñamos a jugar a Dungeons & Dragons en Madrid, pero también dominamos PbtA, Masks, terror, ciencia ficción y sistemas indie.",
			page: "sistemas",
		},
		{
			icon: "ingles.webp",
			title: "Partidas de Rol en Inglés en Madrid",
			text: "Improve your English while playing! La forma más inmersiva de jugar a Dragones y Mazmorras en Madrid, o cualquier otro juego de rol en inglés con un Master de Rol Profesional nativo. Perfect para estudiantes y expats.",
			page: "aprende-ingles",
		},
		{
			icon: "ayuda.webp",
			title: "Cómo Jugar Rol en Madrid ● Guía para Principiantes",
			text: "¿Quieres empezar a jugar a Dungeons & Dragons en Madrid o probar sistemas narrativos? Resolvemos tus dudas para que des tus primeros pasos con la seguridad de un Master de Rol Profesional.",
			page: "ayuda",
		},
	];

	return (
		<>
			<JsonLdLocalBusiness />
			<main className="bg-black text-white">
				<section
					className="relative flex flex-col min-h-screen bg-top bg-cover bg-no-repeat"
					style={{
						backgroundImage: "url('/images/1280px-Role_playing_gamers.webp')",
					}}
				>
					{/* Añadimos meta información en el hero */}
					<div className="flex flex-col justify-top flex-grow py-10">
						<div className="relative z-10 max-w-4xl px-4 mx-auto text-center mt-10">
							<PulsingTitle text={t("hero.title")} />
							<div className="headerFont mb-6 drop-shadow space-y-2">
								<h1 className="text-2xl md:text-3xl">
									<Link href="/category/aventuras" className="hover:text-red-300">
										Partidas de Rol en Madrid
									</Link>
								</h1>

								<h2 className="text-2xl md:text-3xl opacity-90">
									<Link href="/master-de-rol-profesional-madrid" className="hover:text-red-300">
										Master Profesional
									</Link>
								</h2>
							</div>
						</div>
						<div className="relative z-10 max-w-3xl px-4 mx-auto text-center mb-10">
							<HeroButton href={t("hero.link")} text={t("hero.action") || "Reserva Tu Aventura"} />
							<p
								className="bg-black px-6 py-4 rounded text-neutral-200 text-lg"
								style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
							>
								Disfruta de una experiencia de rol única en Madrid: nos desplazamos a tu domicilio o puedes
								venir a{" "}
								<Link href="/rol-en-replay" className="text-red-400 hover:text-red-300">
									nuestro local con cena incluida
								</Link>
								. Partidas{" "}
								<Link href="/master-de-rol-profesional-madrid" className="text-red-400 hover:text-red-300">
									dirigidas por profesionales
								</Link>
								, adaptadas a{" "}
								<Link href="/category/precios" className="text-red-400 hover:text-red-300">
									todos los niveles
								</Link>
								.
							</p>
						</div>
					</div>
				</section>

				<ReviewsSlider />

				<section className="w-full py-10 px-4 bg-gradient-to-b from-black via-gray-900 to-black">
					<div className="max-w-6xl mx-auto text-center mb-16">
						<motion.h2
							className="text-4xl font-amarante text-center mb-6 text-white"
							initial={{ opacity: 0, y: -20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
						>
							<span className="inline-block pb-2 border-b-2 border-red-700">
								Descubre Nuestras Partidas de Rol en Madrid
							</span>
						</motion.h2>

						<motion.p
							className="text-lg text-gray-300 max-w-3xl mx-auto text-center mb-12"
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							transition={{ delay: 0.3 }}
							viewport={{ once: true }}
						>
							¿Buscas algo específico en Madrid? ¡Cuéntanos tu idea! Crearemos una aventura de rol a medida
							para ti o tu grupo.
							<br />
							Abajo encontrarás respuestas a las preguntas frecuentes sobre nuestras partidas.
						</motion.p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{featureItems.map((item, index) => (
							<FeatureItem
								key={index}
								icon={item.icon}
								title={item.title}
								text={item.text}
								page={item.page}
								index={index}
							/>
						))}
					</div>
				</section>
			</main>
		</>
	);
}
